<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import {
  POLL_INTERVAL_MS,
  importStageKey,
  isAbandonableImport,
  isActiveImport,
  statusDotClass as getStatusDotClass,
} from '../importStatus';

const props = defineProps({
  dataImport: {
    type: Object,
    default: null,
  },
  isRefreshing: {
    type: Boolean,
    default: false,
  },
  isRetrying: {
    type: Boolean,
    default: false,
  },
  isAbandoning: {
    type: Boolean,
    default: false,
  },
  isPolling: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['refresh', 'retry', 'abandon']);

const { t } = useI18n();

const pollIntervalSeconds = POLL_INTERVAL_MS / 1000;

const stageLabels = computed(() => ({
  unknown: t('DATA_IMPORTS.MONITOR.STAGES.unknown'),
  queued: t('DATA_IMPORTS.MONITOR.STAGES.queued'),
  contacts: t('DATA_IMPORTS.MONITOR.STAGES.contacts'),
  conversations: t('DATA_IMPORTS.MONITOR.STAGES.conversations'),
  finalizing: t('DATA_IMPORTS.MONITOR.STAGES.finalizing'),
  completed: t('DATA_IMPORTS.MONITOR.STAGES.completed'),
  completed_with_errors: t('DATA_IMPORTS.MONITOR.STAGES.completed_with_errors'),
  failed: t('DATA_IMPORTS.MONITOR.STAGES.failed'),
  abandoned: t('DATA_IMPORTS.MONITOR.STAGES.abandoned'),
}));

const monitorTitle = computed(
  () =>
    stageLabels.value[importStageKey(props.dataImport)] ||
    stageLabels.value.unknown
);

const statusDotClass = computed(() =>
  getStatusDotClass(props.dataImport?.status)
);

const hasActiveImport = computed(() => isActiveImport(props.dataImport));

const canAbandonImport = computed(() => isAbandonableImport(props.dataImport));
</script>

<template>
  <div class="flex flex-wrap items-center justify-between w-full gap-3">
    <div
      class="flex flex-wrap items-center min-w-0 gap-2 text-body-main text-n-slate-11"
    >
      <BackButton compact :button-label="$t('DATA_IMPORTS.DETAIL.BACK')" />
      <span class="w-px h-3 rounded-lg bg-n-strong" />
      <span class="inline-flex items-center gap-1.5">
        <span
          class="rounded-full size-2"
          :class="[statusDotClass, { 'animate-pulse': hasActiveImport }]"
        />
        {{ monitorTitle }}
      </span>
      <template v-if="hasActiveImport">
        <span class="w-px h-3 rounded-lg bg-n-strong" />
        <span class="text-n-teal-11">
          {{
            isPolling
              ? $t('DATA_IMPORTS.MONITOR.REFRESHING')
              : $t('DATA_IMPORTS.MONITOR.LIVE', {
                  seconds: pollIntervalSeconds,
                })
          }}
        </span>
      </template>
    </div>
    <div class="flex items-center gap-2 shrink-0">
      <Button
        v-if="hasActiveImport"
        outline
        slate
        size="sm"
        icon="i-lucide-refresh-cw"
        :is-loading="isRefreshing"
        :aria-label="$t('DATA_IMPORTS.MONITOR.REFRESH')"
        :title="$t('DATA_IMPORTS.MONITOR.REFRESH')"
        @click="$emit('refresh')"
      />
      <Button
        v-if="dataImport?.stalled"
        outline
        slate
        size="sm"
        icon="i-lucide-rotate-ccw"
        :is-loading="isRetrying"
        :disabled="isAbandoning"
        :label="$t('DATA_IMPORTS.TABLE.RETRY')"
        @click="$emit('retry')"
      />
      <Button
        v-if="canAbandonImport"
        ruby
        size="sm"
        :is-loading="isAbandoning"
        :disabled="isRetrying"
        :label="$t('DATA_IMPORTS.TABLE.ABANDON')"
        @click="$emit('abandon')"
      />
    </div>
  </div>
</template>
